import { useQuery } from '@tanstack/react-query';
import { TriangleAlert } from 'lucide-react';
import { useId, useState } from 'react';
import { useApi } from '../api/context';
import { useNetworkRuns } from '../api/queries';
import type { Network, RunChanges } from '../api/types';
import { Button, Dialog } from '../components/ui';
import { formatClock } from '../lib/format';
import { strings } from '../strings';
import { isCurrentPlan, latestFullPlan, useAllAttachments, useRun, useStartNetworkRun } from './page/hooks';
import { componentName, dangerousChanges } from './page/planParts';
import { displayActor } from './shared';

const p = strings.networks.page;

export interface ApplyNetworkDialogProps {
  network: Network;
  /** The plan run to preselect (the run page's "Apply this plan…"); else the network's latest current full plan. */
  planRunId?: string | null;
  onClose: () => void;
}

function changeSummary(changes: RunChanges | null): string {
  const c = changes ?? { create: 0, update: 0, replace: 0, delete: 0 };
  return `+${c.create} ~${c.update} ±${c.replace} −${c.delete}`;
}

/**
 * "Apply <network>" (spec 11 6.15.11, U1.4): Apply this plan (the latest completed full plan, when current) or Apply
 * without a reviewed plan; the plan's dangerous changes, the typed name for them and always without a reviewed plan,
 * and the line about the attached deployments' applies that wait.
 */
export function ApplyNetworkDialog({ network, planRunId, onClose }: ApplyNetworkDialogProps) {
  const api = useApi();
  const runs = useNetworkRuns(network.id, null);
  const attachments = useAllAttachments(network.id);
  const latest = latestFullPlan(runs.data?.items);
  const requested = runs.data?.items.find((r) => r.id === planRunId) ?? null;
  const candidate = requested ?? latest;
  const plan = candidate && isCurrentPlan(candidate, network) ? candidate : null;
  const [choice, setChoice] = useState<'plan' | 'without' | null>(null);
  const mode = choice ?? (plan ? 'plan' : 'without');
  const planRun = useRun(mode === 'plan' ? plan?.id : null);
  const [typed, setTyped] = useState('');
  const { start, busy } = useStartNetworkRun(network);
  const blocks = useQuery({
    queryKey: ['network', network.id, 'validate-current'],
    queryFn: ({ signal }) => api.validateNetwork(network.id, {}, signal),
    retry: false,
  });
  const groupId = useId();
  const typedId = useId();
  const dangerous = mode === 'plan' ? dangerousChanges(planRun.data?.result?.plan?.changes) : [];
  const needsTyped = mode === 'without' || dangerous.length > 0;
  const ready = !busy && (!needsTyped || typed === network.name) && (mode === 'without' || !!plan);
  const applyBlocks = blocks.data?.impact?.applyBlocks;

  return (
    <Dialog
      title={p.applyTitle(network.name)}
      onClose={onClose}
      className="dialog-sheet"
      footer={
        <>
          <Button onClick={onClose}>{strings.common.cancel}</Button>
          <Button
            variant="primary"
            disabled={!ready}
            onClick={() =>
              void start(
                mode === 'plan' && plan ? { kind: 'apply', expectedPlanRunId: plan.id } : { kind: 'apply' },
              ).then((run) => {
                if (run) onClose();
              })
            }
          >
            {p.applySubmit}
          </Button>
        </>
      }
    >
      <fieldset>
        <legend className="sr-only" id={groupId}>
          {p.applyTitle(network.name)}
        </legend>
        <div role="radiogroup" aria-labelledby={groupId} className="space-y-2">
          <label className={`net-apply-option ${plan ? '' : 'is-unavailable'}`}>
            <input
              type="radio"
              name={`${groupId}-mode`}
              checked={mode === 'plan'}
              disabled={!plan}
              onChange={() => setChoice('plan')}
            />
            <span>
              <span className="font-semibold">{p.applyPlan}</span>
              <span className="block text-[12px] text-fg-muted" data-testid="apply-plan-summary">
                {plan
                  ? p.applyPlanSummary(
                      formatClock(plan.finishedAt ?? plan.createdAt),
                      displayActor(plan.createdBy),
                      changeSummary(plan.changes),
                    )
                  : p.noCurrentPlan}
              </span>
            </span>
          </label>
          <label className="net-apply-option">
            <input
              type="radio"
              name={`${groupId}-mode`}
              checked={mode === 'without'}
              onChange={() => setChoice('without')}
            />
            <span>
              <span className="font-semibold">{p.applyWithout}</span>
              <span className="block text-[12px] text-fg-muted">{p.applyWithoutHelp}</span>
            </span>
          </label>
        </div>
      </fieldset>
      {dangerous.length > 0 && (
        <div className="callout callout-danger mt-3" data-testid="apply-dangerous">
          <TriangleAlert aria-hidden className="size-4 shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="font-semibold">{p.dangerous}</p>
            <ul className="list-disc pl-4">
              {dangerous.map((c) => {
                const name = componentName(c.resourceId, attachments.data);
                return (
                  <li key={c.resourceId}>
                    {c.action === 'delete' ? p.deletedComponent(name) : p.recreated(name)}
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      )}
      <p className="mt-3 text-[13px]" data-testid="apply-blocks">
        {applyBlocks
          ? p.blocksLine(applyBlocks.attachedDeployments, applyBlocks.longestStep)
          : p.blocksLineUnknown}
      </p>
      {needsTyped && (
        <div className="mt-3">
          <label className="label" htmlFor={typedId}>
            {p.typeToApply(network.name)}
          </label>
          <input
            id={typedId}
            className="input"
            value={typed}
            autoComplete="off"
            spellCheck={false}
            onChange={(e) => setTyped(e.target.value)}
          />
        </div>
      )}
    </Dialog>
  );
}
